import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import {
  DxAutocompleteModule,
  DxCalendarModule,
  DxDateBoxModule,
  DxNumberBoxModule,
  DxPopupModule,
  DxRadioGroupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTooltipModule,
} from "devextreme-angular";
import { SelectionChangedEvent } from "devextreme/ui/autocomplete";
import { SignalService } from "../../../../../core/services/loader.service";
import { CalendarDatePipe } from "../../../../../core/utils/calendars-date.pipe";
import { DateFormatter } from "../../../../../core/utils/date-formatter";
import { showNotification } from "../../../../../core/utils/toast";
import { Discipline } from "../../../../../domain/models/agreement.model";
import { DisciplineDTO } from "../../../../../domain/models/discipline.model";
import { ProfessionalFigure } from "../../../../../domain/models/figure.model";
import { Geo } from "../../../../../domain/models/geo.model";
import { Resource } from "../../../../../domain/models/personnel.model";
import { SpreadAlgorithm } from "../../../../../domain/models/planning-wizard.model";
import { Practice } from "../../../../../domain/models/practice.model";
import { GetAllActivePEUseCase } from "../../../../../domain/use-cases/hr/get-all-active-pe.usecase";
import { CreateWorkloadFromOpportunityUseCase } from "../../../../../domain/use-cases/workload/create-workload.usecase";

@Component({
  selector: "app-planning-wizard",
  standalone: true,
  imports: [
    CommonModule,
    DxSelectBoxModule,
    DxPopupModule,
    DxDateBoxModule,
    DxSelectBoxModule,
    DxAutocompleteModule,
    DxTextBoxModule,
    ReactiveFormsModule,
    DxRadioGroupModule,
    DxNumberBoxModule,
    DxCalendarModule,
    CalendarDatePipe,
    DxTooltipModule,
  ],
  templateUrl: "./planning-wizard.component.html",
  styleUrl: "./planning-wizard.component.scss",
})
export class PlanningWizardComponent implements OnInit {
  @Input() isPopupVisible: boolean = false;
  @Output() closeModalEmitter = new EventEmitter<number | null>();
  @Input() startDate: string = "";
  @Input() endDate: string = "";
  @Input() figures: ProfessionalFigure[] = [];
  @Input() geos: Geo[] = [];
  @Input() practices: Practice[] = [];
  @Input() disciplines: DisciplineDTO[] = [];
  @Input() agreementId: number = 0;
  @Input() opportunityId: number = 0;
  @Input() pep: { id: number; name: string } = {
    id: 0,
    name: "",
  };

  spreads: {
    id: SpreadAlgorithm;
    desc: "Linear" | "FTE hours" | "Max on start" | "Ramp up";
  }[] = [
    { id: 2, desc: "Linear" },
    { id: 3, desc: "FTE hours" },
    { id: 0, desc: "Max on start" },
    { id: 1, desc: "Ramp up" },
  ];

  filteredDisciplines: Discipline[] = [];
  resources: { id: number; name: string }[] = [];

  localForm!: FormGroup;
  closeButtonOptions;
  saveButtonOptions;
  id!: number;

  minDate: Date = new Date();
  maxDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private createWorkloadFromOpportunityUseCase: CreateWorkloadFromOpportunityUseCase,
    private getAllActivePEUseCase: GetAllActivePEUseCase,
    private loaderService: SignalService,
  ) {
    this.closeButtonOptions = {
      text: "Close",
      stylingMode: "outlined",
      type: "normal",
      onClick: () => {
        this.closeModalEmitter.emit(null);
        this.resetForm();
      },
    };
    this.saveButtonOptions = {
      text: "Save",
      stylingMode: "contained",
      type: "default",
      onClick: () => this.submitForm(),
    };
  }

  ngOnInit(): void {
    this.localForm = this.fb.group(
      {
        petc: this.fb.group({
          id: this.fb.nonNullable.control(this.pep.id, Validators.required),
          hours: this.fb.nonNullable.control(0, Validators.required),
        }),
        figure: this.fb.group({
          practiceId: this.fb.control<number | null>(null),
          disciplineId: this.fb.control<number | null>({
            value: null,
            disabled: true,
          }),
          professionalFigureId: this.fb.control<string | null>(null),
          geoId: this.fb.control<string | null>(null),
          hours: this.fb.control<number | null>({ value: 0, disabled: true }),
        }),
        opp: this.fb.group({
          dateStart: this.fb.nonNullable.control(this.startDate),
          dateEnd: this.fb.nonNullable.control(this.endDate),
          distributionType: this.fb.nonNullable.control(SpreadAlgorithm.Linear),
        }),
      },
      { updateOn: "change" },
    );
  }

  onCloseModal(): void {
    this.closeModalEmitter.emit(null);
    this.resetForm();
  }

  submitForm() {
    const value = this.localForm.value;
    let pepResourceId;
    pepResourceId = value.petc.id == 0 ? this.pep.id : value.petc.id;
    if (
      ((value.figure.disciplineId && value.figure.disciplineId > 0) ||
        (value.figure.professionalFigureId &&
          value.figure.professionalFigureId != "") ||
        (value.figure.geoId && value.figure.geoId != "")) &&
      (!value.figure.hours || value.figure.hours <= 0)
    ) {
      showNotification(
        "If you want to plan a resource please fill all fields and indicate a number of hours, otherwise clear all fields",
        "warning",
      );
      return;
    }

    this.loaderService.loadingSignal.set(true);

    this.createWorkloadFromOpportunityUseCase
      .execute({
        agreementId: this.agreementId,
        wizardValue: {
          opportunityId: this.opportunityId,
          pepResourceId: pepResourceId,
          numberOfHours: value.petc.hours,
          dateStart: value.opp.dateStart,
          dateEnd: value.opp.dateEnd,
          distributionHours: value.opp.distributionType,
          disciplineId: value.figure.disciplineId,
          professionalFigureId: value.figure.professionalFigureId,
          geoId: value.figure.geoId,
          hoursToPlan: value.figure.hours,
        },
      })
      .subscribe((b) => {
        if (b) {
          showNotification("Wizard actions done", "success");
          this.loaderService.loadingSignal.set(false);
          setTimeout(() => {
            this.router
              .navigate(["redirect"], { skipLocationChange: true })
              .then((x) =>
                this.router.navigate(["opportunity", this.agreementId]),
              );
          }, 500);
        } else {
          showNotification(
            "System error - Please contact support team",
            "error",
          );
          this.closeModalEmitter.emit(null);
          this.resetForm();
          this.loaderService.loadingSignal.set(false);
        }
      });
  }

  onPetcSelection(selectedResource: SelectionChangedEvent) {
    if (selectedResource && selectedResource.selectedItem)
      (this.localForm.controls["petc"] as FormGroup).controls["id"].setValue(
        selectedResource.selectedItem.id,
      );
  }

  onPracticeSelection(event: SelectionChangedEvent) {
    if (!!event.selectedItem?.id) {
      this.filteredDisciplines = this.disciplines.filter(
        (d) => d.practice.id == event.selectedItem.id,
      );
      (this.localForm.controls["figure"] as FormGroup).controls[
        "disciplineId"
      ].enable();
    } else
      (this.localForm.controls["figure"] as FormGroup).controls[
        "disciplineId"
      ].disable();
  }

  onSelectionChanged() {
    this.enableHoursControl();
  }

  enableHoursControl() {
    if (
      (this.localForm.value.figure.disciplineId,
      this.localForm.value.figure.professionalFigureId,
      this.localForm.value.figure.geoId)
    )
      (this.localForm.controls["figure"] as FormGroup).controls[
        "hours"
      ].enable();
    else
      (this.localForm.controls["figure"] as FormGroup).controls[
        "hours"
      ].disable();
  }

  resetForm() {
    this.localForm.reset();
    (this.localForm.controls["figure"] as FormGroup).controls[
      "hours"
    ].disable();
    // (this.localForm.controls['figure'] as FormGroup).controls["hours"].setValue(0)
    this.setDates();
  }

  prepareData() {
    this.minDate = new Date(this.startDate);
    this.maxDate = new Date(this.endDate);
    this.getAllActivePEUseCase
      .execute({
        startDate: DateFormatter.toAPIString(new Date(this.startDate)),
        endDate: DateFormatter.toAPIString(new Date(this.endDate)),
      })
      .subscribe((data: Resource[]) => {
        let mappedResources = data.map((el) => ({
          id: el.id,
          name: el.name + " " + el.surname,
        }));
        this.resources = mappedResources;
        this.pep.name =
          this.resources.find((x) => x.id == this.pep.id)?.name ?? "";
      });
    this.setDates();
  }

  setDates() {
    (this.localForm.controls["opp"] as FormGroup).controls[
      "dateStart"
    ].setValue(this.startDate);
    (this.localForm.controls["opp"] as FormGroup).controls["dateEnd"].setValue(
      this.endDate,
    );
  }
}
